import {ChangeDetectorRef, ContentChildren, Directive, Input, QueryList} from "@angular/core";
import {ColumnComponent} from "./column.component";
import {ConfigurationService} from "@fedeltapointofsale/data-client";
import {Subscription} from "rxjs";
import {TranslateService} from "@ngx-translate/core";

export class HideableColumn {
    constructor(public column: string, public title: string) {
    }
}

export class GridState {
    columns: {column: string, enabled: boolean}[];
}

@Directive()
export abstract class AbstractGridComponent {
    @Input() public sortable: boolean = false;
    @Input() public persistenceId: string;

    @ContentChildren(ColumnComponent) public columns: QueryList<ColumnComponent>;

    public hideableColumns: HideableColumn[] = [];
    public enabledColumns: string[] = [];
    public allSelected: boolean = false;

    visibleColumns: ColumnComponent[] = [];

    protected subscriptions: Subscription = new Subscription();

    private gridStateLoaded: boolean = false;
    private hideableColumnsEvaluated: boolean = false;
    private _persistenceMode: string;

    constructor(protected changeDetectorRef: ChangeDetectorRef,
                protected translateService: TranslateService,
                protected configService: ConfigurationService) {
    }

    @Input() get persistenceMode(): string {
        return this._persistenceMode;
    }

    set persistenceMode(persistenceMode: string) {
        if (persistenceMode !== this._persistenceMode) {
            this._persistenceMode = persistenceMode;
        }
        this.loadGridState();
    }

    protected async evaluateHideableColumns(loadGridState: boolean = true): Promise<void> {
        const hideableColumns: HideableColumn[] = [];
        for (const hideableCol of this.columns.filter((col: ColumnComponent) => col.hideable)) {
            const title = await this.translateService.get(hideableCol.title).toPromise();
            hideableColumns.push(new HideableColumn(hideableCol.columnName, title));
        }
        this.hideableColumns = hideableColumns;
        if (loadGridState) {
            await this.loadGridState();
        }
        this.enabledColumns = this.columns
            .filter((col: ColumnComponent) => col.hideable && col.enabled)
            .map((col: ColumnComponent) => col.columnName);
        this.hideableColumnsEvaluated = true;
    }

    public evaluateVisibleColumns(): void {
        if (this.columns) {
            this.visibleColumns = this.columns.filter((col: ColumnComponent) => col.enabled && col.isVisible());
        } else {
            this.visibleColumns = [];
        }
        this.onVisibleColumnsChanged();
        this.changeDetectorRef.detectChanges();
    }

    protected onVisibleColumnsChanged(): void {
    };

    public enabledColumnsChanged(enabledColumns: string[]): void {
        if (!this.hideableColumnsEvaluated) {
            return;
        }
        this.enabledColumns = enabledColumns;
        this.columns
            .filter((col: ColumnComponent) => col.hideable)
            .forEach((col: ColumnComponent) => col.enabled = enabledColumns.includes(col.columnName));
        this.persistGridState();

        this.evaluateVisibleColumns();
    }

    public onHeaderTemplateClick(event: Event): void {
        event.target["parentNode"].click()
    }

    public async toggleEnabled(col: ColumnComponent, event: Event): Promise<void> {
        event.stopPropagation();
        event.preventDefault();
        col.enabled = !col.enabled;
        await this.evaluateHideableColumns(false);
    }

    private persistGridState() {
        if (this.persistenceId && this.hideableColumns.length && this.gridStateLoaded) {
            const gridState: GridState = new GridState();
            gridState.columns = this.columns
                .filter((col: ColumnComponent) => col.hideable)
                .map((col: ColumnComponent) => ({column: col.columnName, enabled: col.enabled}));
            this.configService.saveString(this.configSpec, JSON.stringify(gridState));
        }
    }

    private async loadGridState(): Promise<void> {
        if (this.persistenceId && this.hideableColumns.length) {
            const json: string = await this.configService.getString(this.configSpec);
            if (json) {
                const gridState: GridState = JSON.parse(json);
                this.columns
                    .filter((col: ColumnComponent) => col.hideable)
                    .forEach((col: ColumnComponent) => {
                        const enabled: boolean = gridState.columns
                            .filter((gsc) => gsc.column === col.columnName)
                            .map((gsc) => gsc.enabled)
                            .shift()
                        if (enabled != undefined) {
                            col.enabled = enabled;
                        }
                    });
            }
            this.gridStateLoaded = true;
        }
        this.evaluateVisibleColumns();
    }

    private get configSpec() {
        return "config/grid." + this.persistenceId + "." + (this._persistenceMode || "default");
    }
}
